<template>
    <DsModal
        class="pro-services-page"
        data-qa="pro-services-page"
        size="fullscreen"
        is-open
        show-close
        :title="titleText"
        @close="redirectForClose"
    >
        <div class="centered">
            <section v-if="!successServicesLevel" class="pro-services-page-heading" data-qa="heading-not-purchased">
                <h4>{{ pageHeaderText }}</h4>
                <p>{{ pageHeaderContent }}</p>
            </section>
            <section v-else class="pro-services-page-heading" data-qa="heading-purchased">
                <h4>{{ pageHeaderText }}</h4>
                <p>{{ pageHeaderContent }}</p>
            </section>

            <section class="services-section">
                <service-card
                    v-for="service in servicesData"
                    :key="service.name"
                    :service="service"
                    :selected-level="selectedLevel"
                    :selectable="service.selectable"
                    @select-level="selectLevel"
                />
            </section>

            <DsFilledButton
                class="purchase-button"
                data-qa="checkout-button"
                :disabled="!canProceed"
                :loading="isPurchaseLoading"
                @click="tryOpenCheckout(selectedLevel)"
            >
                {{ $t('proServices.openCheckoutButton') }}
            </DsFilledButton>

            <DsModal
                :is-open="showErrorDialog"
                show-close
                dialog
                size="med"
                :title="$t('proServices.error.title')"
                class="error-dialog message-dialog"
                @close="closeErrorDialog"
            >
                <DsInlineAlert type="critical" leading-icon="alert-circle">
                    <template #default>
                        {{ $t('proServices.error.message') }}
                    </template>

                    <template #footer>
                        <DsFilledButton
                            gray
                            dense
                            trailing-icon="external-link"
                            @click="goToSupportChat"
                        >
                            {{ $t('proServices.error.button') }}
                        </DsFilledButton>
                    </template>
                </DsInlineAlert>
            </DsModal>

            <DsConfirmDialog
                class="permissions-dialog message-dialog"
                :is-open="showPermissionDialog"
                :title="$t('proServices.permissionDialog.header')"
                :message="$t('proServices.permissionDialog.body')"
                :confirm-label="$t('proServices.permissionDialog.confirm')"
                confirm-only
                size="sm"
                @confirm="closeDialog"
            />

            <DsConfirmDialog
                class="subscription-type-dialog message-dialog"
                :is-open="showSubscriptionTypeDialog"
                :title="$t('proServices.subscriptionTypeDialog.header')"
                :message="$t('proServices.subscriptionTypeDialog.body')"
                :cancel-label="$t('proServices.subscriptionTypeDialog.cancel')"
                size="sm"
                @cancel="closeDialog"
            >
                <template #confirm>
                    <DsTextButton
                        data-qa="confirm-button"
                        trailing-icon="external-link"
                        @click="redirectToSales"
                    >
                        {{ $t('proServices.subscriptionTypeDialog.confirm') }}
                    </DsTextButton>
                </template>
            </DsConfirmDialog>

            <DsConfirmDialog
                class="upgrade-dialog message-dialog"
                :is-open="showUpgradeDialog"
                :title="$t('proServices.upgradeDialog.header')"
                :message="$t('proServices.upgradeDialog.body')"
                :cancel-label="$t('proServices.upgradeDialog.cancel')"
                size="sm"
                @cancel="closeDialog"
            >
                <template #confirm>
                    <DsTextButton
                        data-qa="confirm-button"
                        trailing-icon="external-link"
                        @click="redirectToSales"
                    >
                        {{ $t('proServices.upgradeDialog.confirm') }}
                    </DsTextButton>
                </template>
            </DsConfirmDialog>
        </div>
    </DsModal>
</template>

<script>
import ServiceCard from './ServiceCard.vue';
import debounce from 'lodash.debounce';
import { INPUT_DEBOUNCE_DELAY } from '@/components/shared/constants/timing.constants';
import amplitude from '@/components/util/analytics/amplitude';
import axios from 'axios';
import moment from 'moment';

export const PRO_SERVICES_PAGE = 'Pro Services Page';
export const STEP_LOAD = 'Page load';
export const STEP_GET_PAGE_OBJECT = 'Get hosted page object';
export const STEP_CHECKOUT = 'Chargebee checkout';

export default {
    STEP_LOAD,
    STEP_GET_PAGE_OBJECT,
    STEP_CHECKOUT,
    name: 'ProServicesPage',
    components: {
        ServiceCard,
    },
    props: {
        userHasPermissions: Boolean,
        subscriptionInfo: {
            type: Object,
            default: () => {},
        },
    },

    data() {
        return {
            selectedLevel: '',
            delay: INPUT_DEBOUNCE_DELAY,
            showErrorDialog: false,
            showPermissionDialog: false,
            showUpgradeDialog: false,
            showSubscriptionTypeDialog: false,
            chargebeeSiteName: String,
            isPurchaseLoading: false,
        };
    },

    mounted() {
        if (this.selectLevel) {
            this.selectLevel = debounce(this.selectLevel, this.delay, {
                leading: true,
                trailing: false,
            });
        }
        this.selectedLevel = this.successServicesLevel.toLowerCase();
        amplitude.keap.logEvent(amplitude.keap.events.PRO_SERVICES, {
            Action: 'Initial Load',
            'Event Source': 'Success Service Hosted Checkout',
        });
    },

    created() {
        this.load();
    },

    computed: {
        isMonthly() {
            if (!this.subscriptionInfo) {
                return false;
            }
            const termStart = moment.unix(this.subscriptionInfo.currentTermStart);
            const termEnd = moment.unix(this.subscriptionInfo.currentTermEnd);

            return termEnd.diff(termStart, 'days') <= 31;
        },

        canProceed() {
            return this.selectedLevel && this.selectedLevel !== this.successServicesLevel.toLowerCase();
        },

        pageHeader() {
            return this.successServicesLevel === '' ? this.$t('proServices.pageTitle.notPurchased') : this.$t('proServices.pageTitle.purchased');
        },

        successServicesLevel() {
            const purchasedAddons = this.subscriptionInfo?.purchasedAddons || [];

            if (purchasedAddons.some(({ addonType }) => addonType === 'SUCCESS_SERVICES_PLATINUM')) {
                return 'PLATINUM';
            }

            if (purchasedAddons.some(({ addonType }) => addonType === 'SUCCESS_SERVICES_GOLD')) {
                return 'GOLD';
            }

            if (purchasedAddons.some(({ addonType }) => addonType === 'SUCCESS_SERVICES_SILVER')) {
                return 'SILVER';
            }

            return '';
        },

        servicesData() {
            const platinum = this.createServiceData('platinum', '997+', false);
            const gold = this.createServiceData('gold', '499', true);
            const silver = this.createServiceData('silver', '99', true);
            // TODO need to get localized prices from API, not hard coded

            if (this.successServicesLevel === 'PLATINUM') {
                platinum.status = this.$t('proServices.currentPlan');
                platinum.statusType = 'good';
                gold.label = null;

                return { platinum };
            }

            if (this.successServicesLevel === 'GOLD') {
                gold.status = this.$t('proServices.currentPlan');
                gold.statusType = 'good';
                gold.label = null;

                return { gold, platinum };
            }

            if (this.successServicesLevel === 'SILVER') {
                silver.status = this.$t('proServices.currentPlan');
                silver.statusType = 'good';
                gold.label = null;
            }

            return { silver, gold, platinum };
        },

        titleText() {
            return this.$t('proServices.pageTitle');
        },

        pageHeaderText() {
            if (!this.successServicesLevel) {
                return this.$t('proServices.pageHeader.serviceNotPurchased.header');
            }

            return this.$t('proServices.pageHeader.servicePurchased.header');
        },

        pageHeaderContent() {
            if (!this.successServicesLevel) {
                return this.$t('proServices.pageHeader.serviceNotPurchased.content');
            }

            return this.$t('proServices.pageHeader.servicePurchased.content');
        },
    },

    methods: {
        async load() {
            try {
                const { data: chargebeeSiteName } = await axios.get('/app/myAccount/chargebeeSiteName');

                this.chargebeeSiteName = chargebeeSiteName;
                await this.loadChargebeeScript();
            } catch (e) {
                this.showErrorDialog = true;
                this.logError(e, STEP_LOAD);
            }
        },

        termExists(key) {
            return this.$t(key) !== key;
        },

        closeDialog() {
            this.showPermissionDialog = false;
            this.showSubscriptionTypeDialog = false;
            this.showUpgradeDialog = false;
        },

        closeErrorDialog() {
            this.showErrorDialog = false;
        },

        redirectToSales() {
            this.closeDialog();
            window.open('https://calendly.com/membership-services/gsm-strategy-call', '_blank', 'noopener,noreferrer');
        },

        goToSupportChat() {
            window.open('https://help.infusionsoft.com/help/contact-support', '_blank', 'noopener,noreferrer');
        },

        async tryOpenCheckout(level) {
            this.isPurchaseLoading = true;

            if (!this.userHasPermissions) {
                this.showPermissionDialog = true;
                amplitude.keap.logEvent(amplitude.keap.events.PRO_SERVICES, {
                    Action: 'Initial Estimate',
                    'Event Source': 'Success Service Hosted Checkout',
                    Status: 'Error',
                    Scenario: 'Insufficient user permissions',
                });
            } else if (!this.isMonthly) {
                this.showSubscriptionTypeDialog = true;
                amplitude.keap.logEvent(amplitude.keap.events.PRO_SERVICES, {
                    Action: 'Initial Estimate',
                    'Event Source': 'Success Service Hosted Checkout',
                    Status: 'Error',
                    Scenario: 'Annual subscription',
                });
            } else if (this.successServicesLevel) {
                this.showUpgradeDialog = true;
                amplitude.keap.logEvent(amplitude.keap.events.PRO_SERVICES, {
                    Action: 'Initial Estimate',
                    'Event Source': 'Success Service Hosted Checkout',
                    Status: 'Error',
                    Scenario: 'Upgrade attempt',
                });
            } else {
                try {
                    const response = await axios.get(`/app/myAccount/hostedPage/${level}`);

                    if (!response?.data?.hostedPagePayload?.pageInfoJson) {
                        throw new Error('Error fetching hosted checkout for Success Services');
                    }

                    const pageInfo = JSON.parse(response.data.hostedPagePayload.pageInfoJson);

                    this.openCheckout(pageInfo);
                } catch (e) {
                    this.showErrorDialog = true;
                    this.logError(e, STEP_GET_PAGE_OBJECT);
                }
            }

            this.isPurchaseLoading = false;
        },

        openCheckout(hostedPageObject) {
            const chargebeeInstance = window.Chargebee.getInstance();

            chargebeeInstance.openCheckout({
                hostedPage() {
                    return new Promise((resolve) => {
                        resolve(hostedPageObject);
                    });
                },
                loaded: this.checkoutLoaded,
                error: this.checkoutError,
                step: this.checkoutStep,
                success: this.checkoutSuccess,
            });
        },

        selectLevel(level) {
            this.selectedLevel = level === this.selectedLevel ? '' : level;
        },

        checkoutLoaded() {
            amplitude.keap.logEvent(amplitude.keap.events.PRO_SERVICES, {
                Action: 'Initial Estimate',
                'Event Source': 'Success Service Hosted Checkout',
                Status: 'Success',
            });
        },

        checkoutError(error) {
            this.showErrorDialog = true;
            this.logError(error, STEP_CHECKOUT);
        },

        checkoutStep(step) {
            /* Valid steps that can be used here are:
                cart_screen, add_account_info, add_billing_address, review_screen, thankyou_screen
             */
            if (step === 'review_screen') {
                amplitude.keap.logEvent(amplitude.keap.events.PRO_SERVICES, {
                    Action: 'Final Quote',
                    'Event Source': 'Success Service Hosted Checkout',
                    Status: 'Success',
                });
            }
        },

        checkoutSuccess() {
            amplitude.keap.logEvent(amplitude.keap.events.PRO_SERVICES, {
                Action: 'Payment Confirmation',
                'Event Source': 'Payment Confirmation',
                Status: 'Success',
            });

            const purchasedAddons = this.subscriptionInfo.purchasedAddons || [];

            purchasedAddons.push({ addonType: `SUCCESS_SERVICES_${this.selectedLevel.toUpperCase()}` });
            this.subscriptionInfo.purchasedAddons = purchasedAddons;
        },

        createServiceData(level, price, selectable) {
            const retval = {
                name: this.$t(`proServices.services.${level}.name`),
                level,
                selectable,
                price: `$${price}`,
                description: this.$t(`proServices.services.${level}.description`),
                benefits: this.$t(`proServices.services.${level}.benefits`),
            };

            if (this.termExists(`proServices.services.${level}.label`)) {
                retval.label = this.$t(`proServices.services.${level}.label`);
            }

            if (this.termExists(`proServices.services.${level}.button`)) {
                retval.button = {
                    text: this.$t(`proServices.services.${level}.button.text`),
                    url: this.$t(`proServices.services.${level}.button.url`),
                };
            }

            return retval;
        },

        loadChargebeeScript() {
            return new Promise((resolve) => {
                const script = document.createElement('script');

                script.async = true;
                script.src = 'https://js.chargebee.com/v2/chargebee.js';

                script.onload = function () {
                    resolve();
                };

                document.head.appendChild(script);
            }).then(() => {
                window.Chargebee.init({ site: this.chargebeeSiteName });
            });
        },

        logError(e, step) {
            const errorBody = {
                source: PRO_SERVICES_PAGE,
                step,
                error: e.message,
                details: e.stack,
            };

            axios.post('/app/myAccount/logError', errorBody);
        },

        redirectForClose() {
            window.location.href = '/';
        },
    },
};
</script>

<style lang="scss" scoped>
.services-section {
    display: flex;
    gap: $spacing-200;
    flex-wrap: wrap;
    margin-top: $spacing-200;
    padding-bottom: $spacing-300;
}

.centered {
    @include container;
}

.success-plans-header-text {
    font-size: $font-size-lg;
}

.service-card-header {
    display: flex;
}

.service-card-header-item {
    flex-grow: 1;
}

.italic {
    font-style: italic;
}

.purchase-button {
    width: fit-content;
}

.message-dialog p {
    margin-top: $spacing-200;
}

.pro-services-page-heading p {
    max-width: 31.25rem;
    padding-top: $spacing-100;
}
</style>
